import StarIcon from '@mui/icons-material/Star';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { contentsLabel } from '../../api/releaseContents';
import type { CatalogueVersion } from '../../api/types';
import AsyncSection from '../AsyncSection';
import { formatSize } from './formatSize';
import { languageName } from './languageName';

/// Which version of a title to fetch, asked of the person rather than decided for them.
///
/// The order is the server's ranking and the first row says so, because somebody who does not
/// want to read twenty posting names should be able to press the top one and be right. What the
/// rows are told apart by is picture, size, what the rate is worth, and language - and language
/// is where a posting usually says nothing at all, which is said plainly rather than left blank.
///
/// A version that is not what the media search rules asked for is marked as such rather than
/// left out: where nothing matches, the best of the rest is still something to watch, and
/// saying so is the server's job.
export default function VersionChoiceDialog({
  title,
  versions,
  loading,
  error,
  onReload,
  onPick,
  onClose,
}: {
  title: string;
  versions: CatalogueVersion[] | undefined;
  loading: boolean;
  error: Error | undefined;
  onReload: () => void;
  onPick: (version: CatalogueVersion) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('versions.choose.title', { title })}</DialogTitle>

      <DialogContent dividers>
        <AsyncSection loading={loading} error={error} onRetry={onReload}>
          {versions?.length === 0 ? (
            <Typography color="text.secondary">{t('versions.choose.none')}</Typography>
          ) : (
            <Stack spacing={1}>
              <Typography color="text.secondary" variant="body2">
                {t('versions.choose.intro')}
              </Typography>

              <List disablePadding>
                {(versions ?? []).map((version) => (
                  <ListItemButton
                    key={version.id}
                    onClick={() => onPick(version)}
                    sx={{ borderRadius: 1, alignItems: 'flex-start' }}
                  >
                    <ListItemText
                      primary={
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                        >
                          {version.isBest && (
                            <Chip
                              size="small"
                              color="primary"
                              icon={<StarIcon />}
                              label={t('versions.choose.best')}
                            />
                          )}

                          {/* What the rules asked for and what there was are not always the
                              same thing. Where they are not, the version is still offered -
                              and says which it is, rather than passing for what was wanted. */}
                          {!version.matchesRules && (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={t('versions.choose.notAsked')}
                            />
                          )}

                          {contentsLabel(version.contents, t) && (
                            <Chip size="small" variant="outlined" label={contentsLabel(version.contents, t)} />
                          )}

                          <span style={{ overflowWrap: 'anywhere' }}>{version.name}</span>
                        </Stack>
                      }
                      secondary={describe(version, t, i18n.language)}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Stack>
          )}
        </AsyncSection>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}

/// Picture, size, what the rate is worth, language and where it came from: everything one
/// version is told from another by, in the order somebody reads them in.
function describe(version: CatalogueVersion, t: TFunction, locale: string): string {
  const parts: string[] = [];

  if (version.quality.height) {
    parts.push(`${version.quality.height}p`);
  }

  const size = formatSize(version.sizeInBytes);

  if (size) {
    parts.push(size);
  }

  if (version.quality.estimatedBitrate) {
    parts.push(`${Math.round(version.quality.estimatedBitrate / 1_000_000)} Mbit/s`);
  }

  parts.push(languageOf(version, t, locale));

  if (version.source) {
    parts.push(version.source);
  }

  return parts.join(' · ');
}

/// What is known about the soundtrack. A posting that names no language has not said it has
/// none: where the title's own language is known, that is what it most likely carries, and that
/// is said as the guess it is.
function languageOf(version: CatalogueVersion, t: TFunction, locale: string): string {
  const { known, assumed } = version.language;

  if (known.length > 0) {
    const spoken = known.map((code) => languageName(code, locale)).join(', ');

    return version.isMultiLanguage ? t('versions.choose.languageAndMore', { languages: spoken }) : spoken;
  }

  return assumed
    ? t('versions.choose.languageAssumed', { language: languageName(assumed, locale) })
    : t('versions.choose.languageUnknown');
}
